import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link2, Plus } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import { ListViewProductCard } from "../../../ui/components/ListView/components/ListViewProductCard";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth, useRole } from "../../lib/auth/hooks";
import { useWebSocket } from "../../hooks/useWebSocket";
import * as productsApi from "../../lib/api/products";
import type { ProductListItem, ProductStatus } from "../../lib/api/products";
import type { ListParams } from "../../../ui/api/listApi.types";
import { createProductsTableColumns } from "./productsTableColumns";
import { CreateProductModal } from "./CreateProductModal";
import { buildProductSelectionActions } from "./productsBulkActions";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useI18n } from "../../lib/i18n";
import { useListImportExport } from "../data-transfer/useListImportExport";
import * as pageStyles from "../users/UsersListPage.css";
import { useProductsActiveAlertsMap } from "./useProductActiveAlerts";
import { toProductMapPoint } from "./productMapPoint";
import {
  orgContextHash,
  useOrganizationContextOptional,
} from "../../lib/organization-context";

function toCardOrganization(row: ProductListItem) {
  const org = row.access?.owningOrganization;
  return org?.name ? { name: org.name, logoUrl: org.logoUrl } : null;
}

export function ProductsListPage() {
  const { t, tx } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { accessToken, user } = useAuth();
  const { hasRole } = useRole("super-admin");
  const isSuperAdmin = hasRole();
  const orgContext = useOrganizationContextOptional();
  const orgFilterIds = React.useMemo(
    () =>
      orgContext && !orgContext.isAllSelected && orgContext.selectedOrgIds.length > 0
        ? orgContext.selectedOrgIds
        : undefined,
    [orgContext],
  );
  const orgFilterKey = orgFilterIds ? orgContextHash(orgFilterIds) : "all";
  const { subscribe, unsubscribe, isConnected } = useWebSocket();
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [imageVersions, setImageVersions] = React.useState<Map<string, number>>(() => new Map());
  const [lastTelemetryAtMap, setLastTelemetryAtMap] = React.useState<Map<string, string>>(() => new Map());
  const [lastShadowAtMap, setLastShadowAtMap] = React.useState<Map<string, string>>(() => new Map());
  const [serviceStartedAt, setServiceStartedAt] = React.useState<number | undefined>(undefined);
  // Refs so column render closures always see the latest maps without columns changing identity
  const lastTelemetryAtMapRef = React.useRef(lastTelemetryAtMap);
  const lastShadowAtMapRef = React.useRef(lastShadowAtMap);
  const serviceStartedAtRef = React.useRef(serviceStartedAt);
  lastTelemetryAtMapRef.current = lastTelemetryAtMap;
  lastShadowAtMapRef.current = lastShadowAtMap;
  serviceStartedAtRef.current = serviceStartedAt;

  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [visibleProductIds, setVisibleProductIds] = React.useState<string[]>([]);
  const activeAlertsMap = useProductsActiveAlertsMap(visibleProductIds, token);
  const activeAlertsMapRef = React.useRef(activeAlertsMap);
  activeAlertsMapRef.current = activeAlertsMap;

  const refresh = React.useCallback(() => setRefreshKey((k) => k + 1), []);

  // When WS reconnects after a disconnect, refresh the list so lastSignalAtMap
  // is re-seeded from the API and indicators don't stay stale.
  const prevConnected = React.useRef(isConnected);
  React.useEffect(() => {
    if (isConnected && !prevConnected.current) {
      refresh();
    }
    prevConnected.current = isConnected;
  }, [isConnected, refresh]);

  React.useEffect(() => {
    const handler = (msg: { channel?: string; payload?: unknown }) => {
      const productId = msg.channel?.split(':')[2];
      // Shadow settings updates carry shadowTsMs — update the map silently without
      // flashing the row or triggering a full list re-fetch, since shadow health
      // changes are continuous background updates, not discrete user-visible events.
      const isShadowEvent = (msg.payload as { type?: string } | null)?.type === 'shadow.settings';
      if (productId) {
        if (!isShadowEvent) {
          setImageVersions(prev => {
            const next = new Map(prev);
            next.set(productId, (next.get(productId) ?? 0) + 1);
            return next;
          });
        }
        const shadowTsMs = (msg.payload as { shadowTsMs?: number } | null)?.shadowTsMs;
        if (shadowTsMs) {
          const iso = new Date(shadowTsMs).toISOString();
          setLastShadowAtMap(prev => {
            const current = prev.get(productId);
            if (current && current >= iso) return prev;
            const next = new Map(prev);
            next.set(productId, iso);
            return next;
          });
        }
      }
      if (!isShadowEvent) refresh();
    };
    subscribe(['entity:products:*'], handler);
    return () => unsubscribe(['entity:products:*'], handler);
  }, [subscribe, unsubscribe, refresh]);

  React.useEffect(() => {
    const handler = (msg: { type?: string; channel?: string; payload?: unknown }) => {
      if (msg.type !== 'telemetry.update' || !msg.channel) return;
      const productId = msg.channel.split(':')[2];
      const time = (msg.payload as { time?: string | null } | null)?.time;
      if (productId && time) {
        setLastTelemetryAtMap(prev => {
          const next = new Map(prev);
          next.set(productId, time);
          return next;
        });
      }
    };
    subscribe(['telemetry:products:*'], handler);
    return () => unsubscribe(['telemetry:products:*'], handler);
  }, [subscribe, unsubscribe]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = React.useMemo(
    () =>
      createProductsTableColumns(
        lastTelemetryAtMapRef,
        lastShadowAtMapRef,
        serviceStartedAtRef,
        activeAlertsMapRef,
        (orgId) => navigate({ to: "/workspace/organizations/$orgId", params: { orgId } }),
      ),
    [], // stable: render closures read from refs
  );

  React.useEffect(() => {
    if (!token) return;
    const API_BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "http://localhost:3003/api";
    fetch(`${API_BASE}/health/product-service`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data: { startedAt?: string } | null) => {
        if (data?.startedAt) setServiceStartedAt(new Date(data.startedAt).getTime());
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const { confirm, ConfirmModal } = useConfirmModal();

  const [listTotal, setListTotal] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [exportPreviewItems, setExportPreviewItems] = React.useState<
    Array<{ id: string; label: string; matchKey?: string }>
  >([]);

  const selectionActions = React.useMemo(
    () =>
      token
        ? buildProductSelectionActions({ accessToken: token, confirm, onMutationComplete: refresh, isSuperAdmin })
        : [],
    [token, confirm, refresh, isSuperAdmin],
  );

  const {
    headerActions: importExportHeaderActions,
    exportContextAction,
    modals: importExportModals,
  } = useListImportExport({
    entity: "products",
    entityLabel: "Products",
    enableImport: isSuperAdmin,
    enableExport: isSuperAdmin,
    totalCount: listTotal,
    selectedIds,
    previewItems: exportPreviewItems,
    defaultFormat: "json",
    onImported: refresh,
  });

  const selectionActionsWithExport = React.useMemo(() => {
    const base = selectionActions ?? [];
    if (!exportContextAction) return selectionActions;
    return [
      ...base,
      {
        icon: exportContextAction.icon,
        label: exportContextAction.label,
        onClick: (ids: string[]) => exportContextAction.onClick(ids),
      },
    ];
  }, [selectionActions, exportContextAction]);

  const headerActions = React.useMemo(
    () => [
      {
        id: "create",
        label: "Create product",
        icon: <Plus size={14} />,
        color: "primary" as const,
        onClick: () => setShowCreateModal(true),
      },
      ...(isSuperAdmin
        ? [
            {
              id: "smart-link",
              label: t("pro.products.smartLinkSync"),
              icon: <Link2 size={14} />,
              color: "neutral" as const,
              variant: "outline" as const,
              onClick: () => void navigate({ to: "/system/smart-link", search: { tab: "products" } }),
            },
          ]
        : []),
      ...importExportHeaderActions,
    ],
    [importExportHeaderActions, isSuperAdmin, navigate, t],
  );

  const fetchProducts = React.useCallback(
    async (params: ListParams) => {
      if (!token) {
        return { data: [] as ProductListItem[], total: 0 };
      }

      const page = params.page ?? 1;
      const limit = params.limit ?? 50;

      const statusFilter = params.filters?.status;
      const statusList = Array.isArray(statusFilter)
        ? (statusFilter.map(String) as ProductStatus[])
        : undefined;

      const response = await productsApi.fetchProductsList(
        {
          page,
          limit,
          search: params.search?.trim() || undefined,
          status: statusList && statusList.length > 0 ? statusList : undefined,
          organizationIds: orgFilterIds,
        },
        token,
      );
      let items = response.data;

      if (params.sort_by) {
        const key = params.sort_by as keyof ProductListItem;
        const dir = params.order === "desc" ? -1 : 1;
        items = [...items].sort((a, b) => {
          const av = (a[key] ?? "") as string;
          const bv = (b[key] ?? "") as string;
          return av.localeCompare(bv) * dir;
        });
      }

      // Seed signal maps from API data — ensures maps are correct after WS
      // reconnects and on initial load, not just when WS events arrive.
      setLastTelemetryAtMap(prev => {
        let changed = false;
        const next = new Map(prev);
        for (const item of items) {
          if (item.lastTelemetryAt) {
            const current = next.get(item.id);
            if (!current || item.lastTelemetryAt > current) {
              next.set(item.id, item.lastTelemetryAt);
              changed = true;
            }
          }
        }
        return changed ? next : prev;
      });
      setLastShadowAtMap(prev => {
        let changed = false;
        const next = new Map(prev);
        for (const item of items) {
          if (item.lastShadowAt) {
            const current = next.get(item.id);
            if (!current || item.lastShadowAt > current) {
              next.set(item.id, item.lastShadowAt);
              changed = true;
            }
          }
        }
        return changed ? next : prev;
      });

      setExportPreviewItems(
        items.map((p) => ({
          id: p.id,
          label: p.name,
          matchKey: p.serial ?? p.assetTag ?? p.id,
        })),
      );
      setVisibleProductIds(items.map((p) => p.id));
      setListTotal(response.total);
      return { data: items, total: response.total };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey, orgFilterKey],
  );

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="info">{tx("pro.products.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          Products
        </Text>
      </div>

      <div className={pageStyles.tableHost}>
        <ListView<ProductListItem>
          listId="products"
          userId={user?.id ?? "anonymous"}
          columns={columns}
          fetchList={fetchProducts}
          contextKey={`${refreshKey}:${orgFilterKey}`}
          queryOptions={{ staleTime: 0 }}
          fillContainer
          allowFullscreen
          enableSavedViews
          resourceType="products"
          enabledViews={["table", "deck", "stack", "map"]}
          deckCardHeight={316}
          enableSelection
          selectionActions={selectionActionsWithExport}
          onSelectionChange={setSelectedIds}
          getRowLabel={row => row.name ?? row.id}
          searchPlaceholder="Search products by name, serial number, or model…"
          emptyTitle="No products yet"
          emptySubtitle={t("pro.products.emptySubtitle")}
          emptyIcon="battery"
          defaultSort={{ sort_by: "name", order: "asc" }}
          onRowClick={(row) =>
            navigate({ to: "/workspace/products/$productId", params: { productId: row.id } })
          }
          toMapPoint={(row) =>
            toProductMapPoint(row, {
              lastTelemetryAt: lastTelemetryAtMap.get(row.id) ?? row.lastTelemetryAt,
              lastShadowAt: lastShadowAtMap.get(row.id) ?? row.lastShadowAt,
              serviceStartedAt,
              alarmCount: activeAlertsMap.get(row.id)?.length ?? 0,
            })
          }
          onSiteClick={(site) =>
            navigate({ to: "/workspace/products/$productId", params: { productId: String(site.id) } })
          }
          forcedFlashIds={React.useMemo(() => {
            const next = new Map(imageVersions);
            for (const [id, alerts] of activeAlertsMap) {
              const sig = alerts.map((a) => a.code).join("|");
              // Mix alert signature into flash version so table cells re-render on WS updates.
              next.set(id, ((next.get(id) ?? 0) + 1) * 1000 + sig.length + alerts.length);
            }
            return next;
          }, [imageVersions, activeAlertsMap])}
          headerActions={headerActions}
          renderCard={(row, ctx) => (
            <ListViewProductCard
              key={row.id}
              layout="deck"
              product={{ ...row, organization: toCardOrganization(row) }}
              onOrganizationClick={() => {
                const orgId = row.access?.owningOrganization?.id;
                if (orgId) navigate({ to: "/workspace/organizations/$orgId", params: { orgId } });
              }}
              accessToken={token}
              onClick={ctx.onClick}
              refreshKey={(imageVersions.get(row.id) ?? 0) + (ctx.updateGen ?? 0)}
              lastTelemetryAt={lastTelemetryAtMap.get(row.id) ?? null}
              lastShadowAt={lastShadowAtMap.get(row.id) ?? null}
              serviceStartedAt={serviceStartedAt}
              activeAlerts={activeAlertsMap.get(row.id) ?? []}
            />
          )}
          renderStackCard={(row, ctx) => (
            <ListViewProductCard
              key={row.id}
              layout="stack"
              product={{ ...row, organization: toCardOrganization(row) }}
              onOrganizationClick={() => {
                const orgId = row.access?.owningOrganization?.id;
                if (orgId) navigate({ to: "/workspace/organizations/$orgId", params: { orgId } });
              }}
              accessToken={token}
              onClick={ctx.onClick}
              refreshKey={(imageVersions.get(row.id) ?? 0) + (ctx.updateGen ?? 0)}
              lastTelemetryAt={lastTelemetryAtMap.get(row.id) ?? null}
              lastShadowAt={lastShadowAtMap.get(row.id) ?? null}
              serviceStartedAt={serviceStartedAt}
              activeAlerts={activeAlertsMap.get(row.id) ?? []}
            />
          )}
        />
      </div>

      <CreateProductModal
        open={showCreateModal}
        setOpen={setShowCreateModal}
        onCreated={() => {
          setShowCreateModal(false);
          refresh();
        }}
      />
      <ConfirmModal />
      {importExportModals}
    </div>
  );
}
